import * as ActionTypes from './actionTypes'
import {createActionObject} from '../../../redux/utils'
import {rpcCall} from '../../../util/rpc/rpcCalls'
import {
    getCustomerIdsOrder,
    getEditingLevel,
    getEditingUserId,
    getProjectIdsOrder,
    getTaskIdsOrder,
    needMoreCustomers,
    needMoreProjects,
    needMoreTasks,
    showOnlyActiveEntities, getCustomersSearchText, getProjectsSearchText, getTasksSearchText,
} from './selectors'
import * as EntitiesActions from '../../../entities/actions'
import actionsGenerator from '../hierarchyAssignmentsEditor/actionsGenerator'
import {managementAssignmentsCountsLoadSuccess} from '../../../panels/user/userData/actions'
import * as AssignmentsPanelActions from '../editManagementAssignmentsPanel/actions'
import {getUserId} from '../editManagementAssignmentsPanel/selectors'
import {setWorkAssignmentsCountersForUser} from '../workAssignmentsTab/actions'
import * as SyncCallbacks from '../../../panels/user/editPanel/syncCallbacks'
import {handleConcurrentlyRemovedEntitiesAction, hasConcurrentlyRemovedEntities} from '../hierarchyAssignmentsEditor/utils'
import messageResource from '../../../messageResource'
import {getCustomer, getProject, getTask} from '../../../entities/selectors'
import {toast} from '../../../toasts/actions'
import {isWASEnabled} from '../../../features/selectors'
import {addUser} from '../../../entities/actions'
import {AdditionalDataConstants} from '../constants'
import {EXCEPTIONS} from '../../../panels/user/editPanel/util'
import {removeUser} from '../../../entities/actions'
import {handleCommonError} from '../common/actions'
import {handleCommonExceptions} from '../common/utils'

const hierarchyAssignmentsEditorActions = actionsGenerator({
    actionTypes: ActionTypes
})

const LOAD_LIMIT = 20

export const openEditor = () => (dispatch, getState) => {
    const userId = getUserId(getState())
    if (userId !== null) {
        dispatch(hierarchyAssignmentsEditorActions.openEditor(userId))
    }
}

export const closeEditor = hierarchyAssignmentsEditorActions.closeEditor

export const selectCustomer = hierarchyAssignmentsEditorActions.selectCustomer

export const selectProject = hierarchyAssignmentsEditorActions.selectProject

export const deselectCustomer = hierarchyAssignmentsEditorActions.deselectCustomer

export const deselectProject = hierarchyAssignmentsEditorActions.deselectProject

export const loadMoreEntities = () => dispatch => {
    dispatch(loadMoreCustomers())
    dispatch(loadMoreProjects())
    dispatch(loadMoreTasks())
}

export const loadMoreCustomers = () => (dispatch, getState) => {
    const state = getState()
    if (needMoreCustomers(state)) {
        const userId = getEditingUserId(state)
        const onlyActive = showOnlyActiveEntities(state)
        const searchText = getCustomersSearchText(state)
        const offset = getCustomerIdsOrder(state).length

        dispatch(createActionObject(ActionTypes.LOAD_CUSTOMER_ASSIGNMENTS_EDITOR_DATA))
        rpcCall('UserManagementService.getCustomerManagementAssignmentsEditorData', [userId, onlyActive, searchText, offset, LOAD_LIMIT],
            rpcError => handleCommonExceptions(rpcError))
            .then(data => {
                dispatch(EntitiesActions.addCustomers(data['customers'].map(c => c['customer'])))
                dispatch(hierarchyAssignmentsEditorActions.loadCustomersSuccess(data, LOAD_LIMIT))
            })
            .catch(error => {
                dispatch(handleCommonError(userId, error))
                dispatch(hierarchyAssignmentsEditorActions.loadCustomersFailure(error))
            })
    }
}

export const loadMoreProjects = () => (dispatch, getState) => {
    const state = getState()
    if (needMoreProjects(state)) {
        const userId = getEditingUserId(state)
        const customerId = getEditingLevel(state).customerId
        const onlyActive = showOnlyActiveEntities(state)
        const searchText = getProjectsSearchText(state)
        const offset = getProjectIdsOrder(state).length

        dispatch(createActionObject(ActionTypes.LOAD_PROJECT_ASSIGNMENTS_EDITOR_DATA))
        rpcCall('UserManagementService.getProjectManagementAssignmentsEditorData', [userId, customerId, onlyActive, searchText, offset, LOAD_LIMIT],
            rpcError => handleCommonExceptions(rpcError))
            .then(data => {
                dispatch(EntitiesActions.addProjects(data['projects'].map(p => p['project'])))
                dispatch(hierarchyAssignmentsEditorActions.loadProjectsSuccess(data, LOAD_LIMIT))
            })
            .catch(error => {
                dispatch(handleCommonError(userId, error))
                dispatch(hierarchyAssignmentsEditorActions.loadProjectsFailure(error))
            })
    }
}

export const loadMoreTasks = () => (dispatch, getState) => {
    const state = getState()
    if (needMoreTasks(state)) {
        const userId = getEditingUserId(state)
        const projectId = getEditingLevel(state).projectId
        const onlyOpen = showOnlyActiveEntities(state)
        const searchText = getTasksSearchText(state)
        const offset = getTaskIdsOrder(state).length

        dispatch(createActionObject(ActionTypes.LOAD_TASK_ASSIGNMENTS_EDITOR_DATA))
        rpcCall('UserManagementService.getTaskManagementAssignmentsEditorData', [userId, projectId, onlyOpen, searchText, offset, LOAD_LIMIT],
            rpcError => handleCommonExceptions(rpcError))
            .then(data => {
                dispatch(EntitiesActions.addTasks(data['tasks'].map(t => t['task'])))
                dispatch(hierarchyAssignmentsEditorActions.loadTasksSuccess(data, LOAD_LIMIT))
            })
            .catch(error => {
                dispatch(handleCommonError(userId, error))
                hierarchyAssignmentsEditorActions.loadTasksFailure(error)
            })
    }
}

const updateManagementAssignmentsTabCounters = userManagementResult => dispatch => {
    const cptCounters = userManagementResult['additionalResultData']['map']['managementAssignmentsCounters']
    if (cptCounters) {
        dispatch(managementAssignmentsCountsLoadSuccess(cptCounters))
    }
}

export const updateWorkAssignmentsCounters = userManagementResult => dispatch => {
    const userId = userManagementResult['userInfo']['id']
    const workAssignmentsCounters = userManagementResult['additionalResultData']['map']['workAssignmentsCounters']
    if (workAssignmentsCounters) {
        dispatch(setWorkAssignmentsCountersForUser(userId, workAssignmentsCounters))
    }
    dispatch(addUser(userManagementResult['userInfo']))
}

const updateCounters = userManagementResult => dispatch => {
    dispatch(updateManagementAssignmentsTabCounters(userManagementResult))
    dispatch(updateWorkAssignmentsCounters(userManagementResult))
}

export const changeCustomerAssignment = (customerId, assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const isWAFeatureEnabled = isWASEnabled(state)

    dispatch(createActionObject(ActionTypes.CHANGE_CUSTOMER_ASSIGNMENT, {
        customerId,
        assigned
    }))

    const addedCustomerIds = assigned ? [customerId] : []
    const removedCustomerIds = assigned ? [] : [customerId]
    rpcCall('UserManagementService.changeUserCustomerManageAssignments', [userId, addedCustomerIds, removedCustomerIds, true],
        rpcError => handleCommonExceptions(rpcError))
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data))
            } else {
                const {someAssigned, allAssigned} = data.additionalResultData.map[AdditionalDataConstants.ALL_ASSIGNED_INFO]
                dispatch(hierarchyAssignmentsEditorActions.changeCustomerAssignmentSuccess(customerId, assigned, assigned, someAssigned, allAssigned))
                if (!assigned) {
                    dispatch(hierarchyAssignmentsEditorActions.clearCustomersTasksCount([customerId]))
                    dispatch(hierarchyAssignmentsEditorActions.clearCustomersProjectsCount([customerId]))
                }
            }
            SyncCallbacks.onUpdateUserData(data)
            dispatch(updateCounters(data))
            dispatch(AssignmentsPanelActions.clearTabsLoaded())

            if (!assigned && isWAFeatureEnabled) {
                const name = getCustomer(getState(), customerId).name
                dispatch(toast(messageResource.getMessage('user_list.toasts.revoke_manage_customer_from_user', [name])))
            }
        })
        .catch(error => {
            dispatch(handleCommonError(userId, error))
            dispatch(hierarchyAssignmentsEditorActions.changeCustomerAssignmentFailure(customerId, assigned, error))
        })
}

export const changeProjectAssignment = (projectId, assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const customerId = getEditingLevel(state).customerId
    const isWAFeatureEnabled = isWASEnabled(getState())

    dispatch(createActionObject(ActionTypes.CHANGE_PROJECT_ASSIGNMENT, {
        projectId,
        assigned
    }))

    const addedProjectIds = assigned ? [projectId] : []
    const removedProjectIds = assigned ? [] : [projectId]
    rpcCall('UserManagementService.changeUserProjectManageAssignments', [userId, addedProjectIds, removedProjectIds, true, customerId],
        rpcError => handleCommonExceptions(rpcError))
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data))
            } else {
                const {someAssigned, allAssigned} = data.additionalResultData.map[AdditionalDataConstants.ALL_ASSIGNED_INFO]
                dispatch(hierarchyAssignmentsEditorActions.changeProjectAssignmentSuccess(projectId, assigned, assigned, someAssigned, allAssigned))
                if (!assigned) dispatch(hierarchyAssignmentsEditorActions.clearProjectsTasksCount([projectId]))
            }
            SyncCallbacks.onUpdateUserData(data)
            dispatch(updateCounters(data))
            dispatch(AssignmentsPanelActions.clearTabsLoaded())

            if (!assigned && isWAFeatureEnabled) {
                const name = getProject(getState(), projectId).name
                dispatch(toast(messageResource.getMessage('user_list.toasts.revoke_manage_project_from_user', [name])))
            }
        })
        .catch(error => {
            dispatch(handleCommonError(userId, error))
            dispatch(hierarchyAssignmentsEditorActions.changeProjectAssignmentFailure(projectId, assigned, error))
        })
}

export const changeTaskAssignment = (taskId, assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const {customerId, projectId} = getEditingLevel(state)
    const isWAFeatureEnabled = isWASEnabled(state)

    dispatch(createActionObject(ActionTypes.CHANGE_TASK_ASSIGNMENT, {
        taskId,
        assigned
    }))

    const addedTaskIds = assigned ? [taskId] : []
    const removedTaskIds = assigned ? [] : [taskId]
    rpcCall('UserManagementService.changeUserTaskManageAssignments', [userId, addedTaskIds, removedTaskIds, customerId, projectId],
        rpcError => handleCommonExceptions(rpcError))
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data))
            } else {
                const {someAssigned, allAssigned} = data.additionalResultData.map[AdditionalDataConstants.ALL_ASSIGNED_INFO]
                dispatch(hierarchyAssignmentsEditorActions.changeTaskAssignmentSuccess(taskId, assigned, assigned, someAssigned, allAssigned))
            }
            SyncCallbacks.onUpdateUserData(data)
            dispatch(updateCounters(data))
            dispatch(AssignmentsPanelActions.clearTabsLoaded())

            if (!assigned && isWAFeatureEnabled) {
                const name = getTask(getState(), taskId).name
                dispatch(toast(messageResource.getMessage('user_list.toasts.revoke_manage_task_from_user', [name])))
            }
        })
        .catch(error => {
            dispatch(handleCommonError(userId, error))
            dispatch(hierarchyAssignmentsEditorActions.changeTaskAssignmentFailure(taskId, assigned, error))
        })
}

const handleConcurrentlyRemovedEntities = (data, assignAll) => (dispatch) => {
    dispatch(handleConcurrentlyRemovedEntitiesAction({
        getEditingLevel,
        hierarchyAssignmentsEditorActions,
    })(data, assignAll))
}

export const changeAllCustomersAssignments = (assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const isWAFeatureEnabled = isWASEnabled(state)

    dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS))

    rpcCall('UserManagementService.changeAllCustomersManagementAssignments', [userId, assigned],
        rpcError => handleCommonExceptions(rpcError))
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data, true))
            }
            dispatch(hierarchyAssignmentsEditorActions.assignAllCustomersSuccess())
            SyncCallbacks.onUpdateUserData(data)
            dispatch(updateCounters(data))
            dispatch(AssignmentsPanelActions.clearTabsLoaded())

            if (!assigned && isWAFeatureEnabled) {
                dispatch(toast(messageResource.getMessage('user_list.toasts.revoke_all_customers.edit_management_scope')))
            }
        })
        .catch(error => {
            dispatch(handleCommonError(userId, error))
            dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS_FAILURE))
        })
}

export const changeAllProjectsAssignments = (assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const customerId = getEditingLevel(state).customerId
    const isWAFeatureEnabled = isWASEnabled(state)

    dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS))

    rpcCall('UserManagementService.changeAllProjectsManagementAssignments', [userId, customerId, assigned],
        rpcError => handleCommonExceptions(rpcError))
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data, true))
            }
            dispatch(hierarchyAssignmentsEditorActions.assignAllProjectsSuccess())
            SyncCallbacks.onUpdateUserData(data)
            dispatch(updateCounters(data))
            dispatch(AssignmentsPanelActions.clearTabsLoaded())

            if (!assigned && isWAFeatureEnabled) {
                dispatch(toast(messageResource.getMessage('user_list.toasts.revoke_all_projects_from_customer.edit_management_scope')))
            }
        })
        .catch(error => {
            dispatch(handleCommonError(userId, error))
            dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS_FAILURE))
        })
}

export const changeAllTasksAssignments = (assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const {customerId, projectId} = getEditingLevel(state)
    const isWAFeatureEnabled = isWASEnabled(state)

    dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS))

    rpcCall('UserManagementService.changeAllTasksManagementAssignments', [userId, customerId, projectId, assigned],
        rpcError => handleCommonExceptions(rpcError))
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data, true))
            }
            dispatch(hierarchyAssignmentsEditorActions.assignAllTasksSuccess())
            SyncCallbacks.onUpdateUserData(data)
            dispatch(updateCounters(data))
            dispatch(AssignmentsPanelActions.clearTabsLoaded())

            if (!assigned && isWAFeatureEnabled) {
                dispatch(toast(messageResource.getMessage('user_list.toasts.revoke_all_tasks_from_project.edit_management_scope')))
            }
        })
        .catch(error => {
            dispatch(handleCommonError(userId, error))
            dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS_FAILURE))
        })
}

export const changeOnlyActive = onlyActive => dispatch => {
    dispatch(createActionObject(ActionTypes.ONLY_ACTIVE_ENTITIES_SET, onlyActive))
    dispatch(hierarchyAssignmentsEditorActions.clearAll())
    dispatch(hierarchyAssignmentsEditorActions.setCustomersLoaded(false))
    dispatch(hierarchyAssignmentsEditorActions.setProjectsLoaded(false))
    dispatch(hierarchyAssignmentsEditorActions.setTasksLoaded(false))
}

export const changeAllExistingAndFutureProjectsAssignment = (futureProjectsAssigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const customerId = getEditingLevel(state).customerId
    const isWAFeatureEnabled = isWASEnabled(state)

    const addedCustomerIds = futureProjectsAssigned ? [customerId] : []
    const removedCustomerIds = futureProjectsAssigned ? [] : [customerId]

    rpcCall('UserManagementService.changeUserCustomerManageAssignments', [userId, addedCustomerIds, removedCustomerIds, false],
        rpcError => handleCommonExceptions(rpcError))
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data))
            } else {
                dispatch(hierarchyAssignmentsEditorActions.assignAllProjectsSuccess())
            }
            dispatch(createActionObject(ActionTypes.ALL_EXISTING_AND_FUTURE_PROJECTS, futureProjectsAssigned))
            SyncCallbacks.onUpdateUserData(data)
            dispatch(updateCounters(data))
            dispatch(AssignmentsPanelActions.clearTabsLoaded())

            if (!futureProjectsAssigned && isWAFeatureEnabled) {
                const name = getCustomer(getState(), customerId).name
                dispatch(toast(messageResource.getMessage('user_list.toasts.revoke_manage_customer_from_user', [name])))
            }
        })
        .catch(error => {
            dispatch(handleCommonError(userId, error))
            dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS_FAILURE))
        })
}

export const changeAllExistingAndFutureTasksAssignment = (futureTasksAssigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const {customerId, projectId} = getEditingLevel(state)
    const isWAFeatureEnabled = isWASEnabled(state)

    const addedProjectIds = futureTasksAssigned ? [projectId] : []
    const removedProjectIds = futureTasksAssigned ? [] : [projectId]

    rpcCall('UserManagementService.changeUserProjectManageAssignments', [userId, addedProjectIds, removedProjectIds, false, customerId],
        rpcError => handleCommonExceptions(rpcError))
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data))
            } else {
                dispatch(hierarchyAssignmentsEditorActions.assignAllTasksSuccess())
            }
            dispatch(createActionObject(ActionTypes.ALL_EXISTING_AND_FUTURE_TASKS, futureTasksAssigned))
            SyncCallbacks.onUpdateUserData(data)
            dispatch(updateCounters(data))
            dispatch(AssignmentsPanelActions.clearTabsLoaded())

            if (!futureTasksAssigned && isWAFeatureEnabled) {
                const name = getProject(getState(), projectId).name
                dispatch(toast(messageResource.getMessage('user_list.toasts.revoke_manage_project_from_user', [name])))
            }
        })
        .catch(error => {
            dispatch(handleCommonError(userId, error))
            dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS_FAILURE))
        })
}

export const changeCustomersSearchText = hierarchyAssignmentsEditorActions.changeCustomersSearchText
export const clearCustomersSearchText = hierarchyAssignmentsEditorActions.clearCustomersSearchText

export const changeProjectsSearchText = hierarchyAssignmentsEditorActions.changeProjectsSearchText
export const clearProjectsSearchText = hierarchyAssignmentsEditorActions.clearProjectsSearchText

export const changeTasksSearchText = hierarchyAssignmentsEditorActions.changeTasksSearchText
export const clearTasksSearchText = hierarchyAssignmentsEditorActions.clearTasksSearchText



// WEBPACK FOOTER //
// ./web/js/administration/userManagement/managementAssignmentsEditor/actions.js